import { createBrowserRouter, Outlet } from 'react-router'
import { lazy, Suspense, type ReactNode } from 'react'
import { AuthProvider, registerSprintRoutes } from '@waago/api'
import { MobileFrame } from '@waago/ui'
import { AppTabs } from '../components/AppTabs'
import { ProviderGate } from '../components/ProviderGate'
import { SprintGate } from '../components/SprintGate'
import { PROVIDER_SPRINTS } from './sprints'
import { RequireAuth } from './RequireAuth'
import LoginPage from './pages/LoginPage'

// Installed before the router renders, so the first navigation already knows
// what this build is showing.
registerSprintRoutes(PROVIDER_SPRINTS)

const SignupWizard = lazy(() => import('./pages/SignupWizard'))
const WelcomePage = lazy(() => import('./pages/WelcomePage'))
const ReportPage = lazy(() => import('./pages/ReportPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const OffersPage = lazy(() => import('./pages/OffersPage'))
const MissionsPage = lazy(() => import('./pages/MissionsPage'))
const MissionDetailPage = lazy(() => import('./pages/MissionDetailPage'))
const ServicesPage = lazy(() => import('./pages/ServicesPage'))
const ServiceWizardPage = lazy(() => import('./pages/ServiceWizardPage'))
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'))
const OfferDetailPage = lazy(() => import('./pages/OfferDetailPage'))
const CommissionMethodPage = lazy(() => import('./pages/CommissionMethodPage'))
const PlanDetailPage = lazy(() => import('./pages/PlanDetailPage'))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'))
const CampaignFormPage = lazy(() => import('./pages/CampaignFormPage'))
const VehiclesPage = lazy(() => import('./pages/VehiclesPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const RevenuePage = lazy(() => import('./pages/RevenuePage'))
const PayoutPage = lazy(() => import('./pages/PayoutPage'))
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'))
const AddEmployeePage = lazy(() => import('./pages/AddEmployeePage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const SecurityPage = lazy(() => import('./pages/SecurityPage'))
const PhoneChangePage = lazy(() => import('./pages/PhoneChangePage'))
const LanguagePage = lazy(() => import('./pages/LanguagePage'))
const PermissionsPage = lazy(() => import('./pages/PermissionsPage'))
const AidePage = lazy(() => import('./pages/AidePage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))

function Loading() {
  return (
    <p className="animate-slide-up p-8 text-center text-sm text-slate-500" role="status">
      Chargement…
    </p>
  )
}

/**
 * The one place the session lives. See the note on the client app's Root: a
 * provider mounted per route branch re-exchanges the refresh cookie on every
 * navigation between them, and two rotations of one token look like a stolen
 * one.
 */
function Root() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

function Shell() {
  return (
    <RequireAuth>
      <Suspense fallback={<Loading />}>
        <AppTabs />
      </Suspense>
    </RequireAuth>
  )
}

/**
 * Account settings pages are full-screen in the design — a back arrow, not a
 * tab bar — and are places a provider leaves rather than navigates away from.
 * Keeping the tab bar under them would offer a second way out the design
 * does not have.
 */
function Overlay({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <MobileFrame fill>
        <main id="main" className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <ProviderGate>
            <SprintGate>
              <Suspense fallback={<Loading />}>{children}</Suspense>
            </SprintGate>
          </ProviderGate>
        </main>
      </MobileFrame>
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        // The app's first screen (frame `4076:2753`). Before the sign-in
        // rather than behind it: somebody who has not signed up yet needs to
        // be told what this is.
        path: '/bienvenue',
        element: (
          <Suspense fallback={<Loading />}>
            <WelcomePage />
          </Suspense>
        ),
      },
      {
        path: '/connexion',
        element: <LoginPage />,
      },
      {
        path: '/inscription',
        element: (
          <Suspense fallback={<Loading />}>
            <SignupWizard />
          </Suspense>
        ),
      },
      {
        path: '/',
        element: <Shell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'offres', element: <OffersPage /> },
          { path: 'missions', element: <MissionsPage /> },
          { path: 'services', element: <ServicesPage /> },
          { path: 'vehicules', element: <VehiclesPage /> },
          { path: 'abonnement', element: <SubscriptionPage /> },
          { path: 'revenus', element: <RevenuePage /> },
          { path: 'revenus/virements', element: <PayoutPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'compte', element: <AccountPage /> },
          { path: 'aide', element: <AidePage /> },
        ],
      },
      {
        path: '/aide/contact',
        element: (
          <Overlay>
            <ContactPage />
          </Overlay>
        ),
      },
      {
        // "Formulaire de signalement" (frames `4789:3997`, `4789:4100`).
        path: '/aide/signalement',
        element: (
          <Overlay>
            <Suspense fallback={<Loading />}>
              <ReportPage />
            </Suspense>
          </Overlay>
        ),
      },
      {
        path: '/missions/:id',
        element: (
          <Overlay>
            <MissionDetailPage />
          </Overlay>
        ),
      },
      {
        path: '/services/nouveau',
        element: (
          <Overlay>
            <ServiceWizardPage />
          </Overlay>
        ),
      },
      {
        // "Détails sur la mission" for an offer still open (frames `4546:1000`,
        // `4898:6095`, `5484:3790`).
        path: '/offres/:id',
        element: (
          <Overlay>
            <OfferDetailPage />
          </Overlay>
        ),
      },
      {
        // "Détails du service" (frames `4639:1266`, `5197:6420`, `5659:3860`).
        path: '/services/:id',
        element: (
          <Overlay>
            <ServiceDetailPage />
          </Overlay>
        ),
      },
      {
        // "Abonnement" — one plan in full (frame `5027:2714`).
        path: '/abonnement/:code',
        element: (
          <Overlay>
            <PlanDetailPage />
          </Overlay>
        ),
      },
      {
        // "Paiement" — mode de prélèvement de la commission (frame `4767:1893`).
        path: '/compte/paiement',
        element: (
          <Overlay>
            <CommissionMethodPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/documents',
        element: (
          <Overlay>
            <DocumentsPage />
          </Overlay>
        ),
      },
      {
        path: '/publicite',
        element: (
          <Overlay>
            <CampaignsPage />
          </Overlay>
        ),
      },
      {
        path: '/publicite/:id',
        element: (
          <Overlay>
            <CampaignFormPage />
          </Overlay>
        ),
      },
      {
        path: '/employes',
        element: (
          <Overlay>
            <EmployeesPage />
          </Overlay>
        ),
      },
      {
        path: '/employes/ajouter',
        element: (
          <Overlay>
            <AddEmployeePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/profil',
        element: (
          <Overlay>
            <ProfilePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/securite',
        element: (
          <Overlay>
            <SecurityPage />
          </Overlay>
        ),
      },
      {
        path: '/compte/securite/numero',
        element: (
          <Overlay>
            <PhoneChangePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/langue',
        element: (
          <Overlay>
            <LanguagePage />
          </Overlay>
        ),
      },
      {
        path: '/compte/permission',
        element: (
          <Overlay>
            <PermissionsPage />
          </Overlay>
        ),
      },
    ],
  },
])
